import { useCallback, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useFormData } from 'brief-form'
import { routes } from 'commons/constants/routes'
import { determineStateData } from 'commons/utils'
import { Request } from 'commons/utils/request/request'
import { Toast } from 'ui/components/Toast'

import { handlerServerError } from '@orbit/errors'

import { CompanyForm } from '../../../../../../../typings'

const request = (company: CompanyForm) => {
  const fn = company.id ? Request.put : Request.post
  return fn(Request.urls.bank.company, company)
}

export const useManageCompanyForm = (
  initialValue: CompanyForm,
  onDelete: (company: { id: number | null; name: string | null }) => void
) => {
  const navigate = useNavigate()

  const { config, Field, validate, isDirty } = useFormData<CompanyForm>({
    initialValue,
    onBeforeChange: ({ value, errors, oldValue }) => {
      if (value.country !== oldValue.country) {
        const states = determineStateData(value.country)
        if (states.length === 1) {
          value.state = states[0].value
        } else if (states.find((el) => el.value === value.state) === undefined) {
          value.state = ''
        }
      }

      if (value.mailingCountry !== oldValue.mailingCountry) {
        const states = determineStateData(value.mailingCountry)
        if (states.length === 1) {
          value.mailingState = states[0].value
        } else if (states.find((el) => el.value === value.mailingState) === undefined) {
          value.mailingState = ''
        }
      }

      return {
        value,
        errors
      }
    }
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const { mutate: saveCompany } = useMutation<CompanyForm, Error, CompanyForm>({
    mutationFn: (company: CompanyForm) => request(company),
    onSuccess: async (request, response) => {
      Toast.successSmall('Company successfully saved')
      if (request.id) {
        navigate({
          to: routes.bank.relationships.details,
          params: { id: request.id.toString() }
        })
      } else if (response.id) {
        navigate({
          to: routes.bank.relationships.details,
          params: { id: response.id.toString() }
        })
      }
    },
    onError: async (error) => handlerServerError<CompanyForm>(error, { form: config }),
    onSettled: async () => setIsSubmitting(false)
  })

  const submitHandler = useCallback(() => {
    if (validate({ updateFields: true }).valid) {
      setIsSubmitting(true)
      return saveCompany(config.value)
    }

    return Promise.resolve(null)
  }, [config.value, validate, saveCompany])

  const deleteHandler = useCallback(() => onDelete(config.value), [onDelete, config.value])

  return {
    config,
    Field,
    submitHandler,
    deleteHandler,
    isSubmitting,
    isDirty
  }
}
